import React, { useEffect, useState } from 'react';
import { FieldErrors } from 'react-hook-form';
import { ToastContainer } from 'react-toastify';
import { useFormControls } from '@features/onboarding/hooks/useFormControls';
import { useFinalSubmissionStore } from 'src/store/final-submission';
import { useUnderReviewStore } from 'src/store/under-review';
import { useTourStore } from 'src/store/tour';
import { useAuthStore } from 'src/store/auth';
import { useApplicationStore } from 'src/store/application';
import { useAcceptanceChangeStore } from 'src/store/acceptance-change';
import { UnderReview, AfterSignIn, TourGuide, AcceptanceWarningModal } from '@features/onboarding/components/shared/modal';
import { OnboardingSchema } from '@features/onboarding/schema/onboarding';
import { applicationService } from 'src/services/application';

export const OnboardingForm = () => {
  const {
    selectedStep,
    currentFormStep,
    formSteps,
    handleNextStep,
    setData,
    setTotalSteps,
  } = useFormControls();

  const { underwritingResult } = useFinalSubmissionStore();
  const { 
    isModalOpen: isUnderReviewModalOpen,
    setModalOpen: setResetModalOpen,
  } = useUnderReviewStore();

  const {
    isTourGuideOpen,
    openTourGuide,
    checkShouldShowTour,
  } = useTourStore();
  const { isAuthenticated } = useAuthStore();
  const { signedAt } = useApplicationStore();
  const {
    isModalOpen: isAcceptanceWarningModalOpen,
    setModalOpen: setAcceptanceWarningModalOpen,
  } = useAcceptanceChangeStore();
  const StepComponent = currentFormStep?.Component;
  const isLastStep = selectedStep === formSteps?.length - 1;

  // remember data when user change step
  const handleChange = React.useCallback(
    (data: Partial<OnboardingSchema>) => {
      setData(data);
    },
    [setData]
  );

  const handleSubmitForm = async (data: Partial<OnboardingSchema>) => {
    setData(data);
  };

  function getFirstErrorMessage(obj: FieldErrors): string | null {
    if (typeof obj !== 'object' || obj === null) {
      return null;
    }

    if (obj?.message) {
      return obj.message as unknown as string;
    }

    const keys = Object.keys(obj);
    if (keys.length === 0) {
      return null;
    }

    const firstKey = keys[0];
    const firstValue = obj[firstKey];

    if (typeof firstValue === 'object' && firstValue !== null) {
      const message = getFirstErrorMessage(firstValue as FieldErrors);
      if (message) {
        return message;
      }
    }

    return null;
  }
  const [formErrorMessage, setFormErrorMessage] = useState<string | null>(null);
  // const handleInvalid = (errors: FieldErrors) => {
  //   const errorMessage = getFirstErrorMessage(errors);
  //   if (errorMessage) {
  //     toast.error(errorMessage);
  //   }
  // };
  const handleInvalid = (errors: FieldErrors) => {
    const errorMessage = getFirstErrorMessage(errors);
    setFormErrorMessage(errorMessage || null);
  };
  useEffect(() => {
    setTotalSteps(formSteps.length);
  }, [formSteps.length, setTotalSteps]);

  const { resetState } = useFinalSubmissionStore();

  useEffect(() => {
    if (!isLastStep && underwritingResult?.status === 'rejected') {
      resetState();
    }
  }, [isLastStep]);

  // Check when underwriting result changes to show under review modal
  useEffect(() => {
    if (underwritingResult?.status === 'human_review') {
      setResetModalOpen(true);
    }
  }, [underwritingResult, setResetModalOpen]);

  // Check application state for phqUnderReviewAt and phqAcceptedAt
  useEffect(() => {
    const checkApplicationState = async () => {
      try {
        const applicationState = await applicationService.applicationState();

        // Check if application is accepted (similar to under-review logic)
        if (applicationState?.phqAcceptedAt || applicationState?.phqRejectedAt || applicationState?.phqUnderReviewAt) {
          useAcceptanceChangeStore.getState().checkApplicationStatus();
        }
      } catch (error) {
        console.error('Failed to check application state:', error);
      }
    };

    if (isAuthenticated) {
      checkApplicationState();
    }
  }, [isAuthenticated, setResetModalOpen, setAcceptanceWarningModalOpen]);

  // Show tour guide after authentication and if conditions are met
  useEffect(() => {
    if (isAuthenticated && checkShouldShowTour()) {
      // Small delay to ensure the page is fully loaded
      const timer = setTimeout(() => {
        openTourGuide();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, checkShouldShowTour, openTourGuide]);

  return (
    <div id="step-parent" className="h-full relative overflow-y-clip max-md:mb-[100px] max-2xl:mb-[82px]">
      {StepComponent && (
        <StepComponent
          title={currentFormStep?.title}
          sectionName={currentFormStep?.sectionName}
          description={currentFormStep?.description}
          subscription={currentFormStep?.subscription}
          onSubmit={isLastStep ? handleSubmitForm : handleNextStep}
          onChange={handleChange}
          onInvalid={handleInvalid}
          externalErrorMessage={formErrorMessage}
        />
      )}
      <ToastContainer />
      {isUnderReviewModalOpen && (
        <UnderReview />
      )}
      {isTourGuideOpen && (
        <TourGuide />
      )}
      {isAcceptanceWarningModalOpen && (
        signedAt === null ? <AcceptanceWarningModal /> : <AfterSignIn />
      )}
    </div>
  );
};
